import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref } from 'vue'

export type Theme = 'light' | 'dark' | 'system'

export interface Toast {
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    message: string
    duration?: number
}

const THEME_STORAGE_KEY = 'rws-theme'

/**
 * UI store - manages global UI state (theme, toasts, sidebar, loading)
 */
export const useUiStore = defineStore('ui', () => {
    // State
    const sidebarCollapsed = ref(false)
    const isLoading = ref(false)
    const toasts = ref<Toast[]>([])
    const theme = ref<Theme>('light')
    const isDarkMode = ref(false)

    // Initialize theme from localStorage or system preference
    function initializeTheme() {
        const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null

        if (savedTheme) {
            theme.value = savedTheme
        } else {
            // Default to dark mode
            theme.value = 'dark'
        }

        applyTheme()
    }

    // Apply theme to document
    function applyTheme() {
        const effectiveTheme = theme.value === 'system'
            ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : theme.value

        isDarkMode.value = effectiveTheme === 'dark'

        if (effectiveTheme === 'dark') {
            document.documentElement.classList.add('dark-mode')
            document.documentElement.classList.remove('light-mode')
        } else {
            document.documentElement.classList.remove('dark-mode')
            document.documentElement.classList.add('light-mode')
        }
    }

    // Set theme
    function setTheme(newTheme: Theme) {
        theme.value = newTheme
        localStorage.setItem(THEME_STORAGE_KEY, newTheme)
        applyTheme()
    }

    // Toggle between light and dark
    function toggleTheme() {
        setTheme(isDarkMode.value ? 'light' : 'dark')
    }

    // Watch for system theme changes
    if (typeof window !== 'undefined') {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
            if (theme.value === 'system') {
                applyTheme()
            }
        })
    }

    // Sidebar actions
    function toggleSidebar() {
        sidebarCollapsed.value = !sidebarCollapsed.value
    }

    function setSidebarCollapsed(collapsed: boolean) {
        sidebarCollapsed.value = collapsed
    }

    function setLoading(loading: boolean) {
        isLoading.value = loading
    }

    // Toast actions
    function showToast(toast: Omit<Toast, 'id'>) {
        const id = Date.now().toString()
        toasts.value.push({ ...toast, id })

        const duration = toast.duration ?? 5000
        setTimeout(() => {
            removeToast(id)
        }, duration)
    }

    function removeToast(id: string) {
        const index = toasts.value.findIndex(t => t.id === id)
        if (index > -1) {
            toasts.value.splice(index, 1)
        }
    }

    function success(message: string) {
        showToast({ type: 'success', message })
    }

    function error(message: string) {
        showToast({ type: 'error', message, duration: 8000 })
    }

    function warning(message: string) {
        showToast({ type: 'warning', message })
    }

    function info(message: string) {
        showToast({ type: 'info', message })
    }

    return {
        // State
        sidebarCollapsed,
        isLoading,
        toasts,
        theme,
        isDarkMode,
        // Theme actions
        initializeTheme,
        setTheme,
        toggleTheme,
        // Sidebar actions
        toggleSidebar,
        setSidebarCollapsed,
        setLoading,
        // Toast actions
        showToast,
        removeToast,
        success,
        error,
        warning,
        info,
    }
})

// Preserve UI state during Vite HMR (dev-only)
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUiStore, import.meta.hot))
}
